import React, { useState, useEffect, useRef, useContext } from 'react';
import { useTranslation } from "react-i18next";
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Tooltip } from 'primereact/tooltip';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { MultiSelect } from 'primereact/multiselect';
import { useSelector } from 'react-redux';
import { AuthContext } from '../../../../Header/auth - context';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import Loading from '../../../../Loading/loading';
import BarcodeScanner from '../Nova_prijemnica/BarcodeScanner';
import { InputNumber } from 'primereact/inputnumber';
import CustomDropdown from '../../../../custom/customDropdown';

import '../../magacin_basic.css';
import '../../magacin.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

const PregledGuma = () => {
  const { t } = useTranslation();
  const toast = useRef(null);
  const auth = useContext(AuthContext);
  const company = useSelector((state) => state.company);

  const [tableData, setTableData] = useState([]);
  const [allColumns, setAllColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [originalColumnOrder, setOriginalColumnOrder] = useState([]);
  const [globalFilter, setGlobalFilter] = useState(null);
  const [first, setFirst] = useState(0);
  /* Broj redova po strani bira korisnik; 25 je polazno. */
  const [redovaPoStrani, setRedovaPoStrani] = useState(25);
  const [load, setLoad] = useState(true);
  const [selectedRowId, setSelectedRowId] = useState(null);

  const [scannerVisible, setScannerVisible]         = useState(false);
  const [scannerKey, setScannerKey]                 = useState(0);
  const [gumaStatusVisible, setGumaStatusVisible]   = useState(false);
  const [gumaStatusData, setGumaStatusData]         = useState(null);
  const [gumaStatusLoading, setGumaStatusLoading]   = useState(false);

  const [magacinOptions, setMagacinOptions]         = useState([]);
  const [vratiVisible, setVratiVisible]             = useState(false);
  const [vratiMagacin, setVratiMagacin]             = useState(null);
  const [vratiKilometraza, setVratiKilometraza]     = useState(0);
  const [vratiSubmitted, setVratiSubmitted]         = useState(false);
  const [vratiLoading, setVratiLoading]             = useState(false);

  const getId = (row) => row?.Izaberi ?? row?.id ?? null;
  const isSelected = (row) => String(getId(row)) === String(selectedRowId);
  const isButtonColumn = (field) => field.startsWith('Izaberi');

  const chooseItem = (rowData) => {
    const id = getId(rowData);
    setSelectedRowId((prev) => (String(prev) === String(id) ? null : String(id)));
  };

  const previewBodyTemplate = (rowData) => (
    <Button
      label={t('nova.izaberi')}
      icon="pi pi-check"
      type="button"
      className="p-button-text p-button-sm"
      onClick={() => chooseItem(rowData)}
    />
  );

  const handleErrors = (response) => {
    if (response.ok) return response.json();
    throw response;
  };

  const fetchData = () => {
    setLoad(true);
    fetch(process.env.REACT_APP_BACKEND_URL + '/warehouse/GetGumaPregled', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
      body: JSON.stringify({}),
    })
      .then(handleErrors)
      .then((result) => {
        setTableData(result || []);
        if (result && result.length > 0) {
          const cols = Object.keys(result[0])
            .map((k) => ({ label: k, value: k }));
          setAllColumns(cols);
          const order = cols.map((c) => c.value);
          setOriginalColumnOrder(order);
          setSelectedColumns(order);
        } else {
          setAllColumns([]);
          setSelectedColumns([]);
          setOriginalColumnOrder([]);
        }
        setLoad(false);
      })
      .catch((err) => {
        err?.json?.().then((e) =>
          toast.current.show({ severity: 'error', summary: 'Greška', detail: e.message, life: 3000 })
        );
        setLoad(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, [auth.token, company.companyId]); // eslint-disable-line

  useEffect(() => {
    fetch(process.env.REACT_APP_BACKEND_URL + '/warehouse/GetWarehouseAndTypes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
      body: JSON.stringify({}),
    })
      .then(handleErrors)
      .then((data) => {
        const parsed = JSON.parse(data[0]['']);
        setMagacinOptions(parsed?.MagacinDB || []);
      })
      .catch(() => {});
  }, [auth.token]); // eslint-disable-line

  const handleDelete = () => {
    if (!selectedRowId) return;
    confirmDialog({
      message: 'Jel ste sigurni da želite pasivizirati izabranu gumu?',
      acceptLabel: 'Da',
      rejectLabel: 'Ne',
      icon: 'pi pi-info-circle',
      acceptClassName: 'p-button-danger',
      accept: acceptDelete,
    });
  };

  const acceptDelete = () => {
    fetch(process.env.REACT_APP_BACKEND_URL + '/warehouse/DeleteGuma', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
      body: JSON.stringify({ id: selectedRowId }),
    })
      .then(handleErrors)
      .then((data) => {
        const msg = Array.isArray(data) ? data[0]?.message : data?.message;
        if (msg && msg !== 'Uspešno pasiviziranje gume.') {
          toast.current.show({ severity: 'warn', summary: msg, life: 3000 });
          return;
        }
        toast.current.show({ severity: 'success', summary: 'Guma je uspešno pasivizirana!', life: 3000 });
        setSelectedRowId(null);
        fetchData();
      })
      .catch((err) =>
        err?.json?.().then((e) =>
          toast.current.show({ severity: 'error', summary: 'Greška', detail: e.message, life: 3000 })
        )
      );
  };

  const rowClassName = (rowData) => {
    const inWarehouse = rowData.Raspolozivost === 'DA';
    if (isSelected(rowData)) return inWarehouse ? 'row-guma-selected' : 'selected-row';
    return inWarehouse ? 'row-guma-available' : '';
  };

  const exportExcel = async () => {
    if (!selectedColumns.length || !tableData?.length) {
      toast.current.show({ severity: 'warn', summary: 'Nema podataka za export.', life: 3000 });
      return;
    }
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Pregled guma');
    const headerRow = worksheet.addRow(selectedColumns);
    headerRow.font = { bold: true };
    headerRow.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFEFEF' } };
      cell.border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } };
    });
    tableData.forEach((row) => {
      const r = worksheet.addRow(selectedColumns.map((h) => row[h] ?? ''));
      r.eachCell((cell) => {
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      });
    });
    worksheet.columns.forEach((col, i) => {
      const lengths = [selectedColumns[i]?.length || 10, ...tableData.map((r) => (r[selectedColumns[i]] ?? '').toString().length)];
      col.width = Math.min(Math.max(...lengths) + 2, 40);
    });
    const date = new Date();
    const dt = date.toLocaleString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Belgrade' }).replace(/,/g, '');
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `Pregled guma - ${dt}.xlsx`);
  };

  const handleScan = (code) => {
    setScannerVisible(false);
    setGumaStatusLoading(true);
    setGumaStatusData(null);
    setGumaStatusVisible(true);
    fetch(process.env.REACT_APP_BACKEND_URL + '/warehouse/GetGumaBySerBroj', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
      body: JSON.stringify({ serBroj: code }),
    })
      .then(handleErrors)
      .then((data) => {
        const guma = Array.isArray(data) && data.length > 0 ? data[0] : null;
        setGumaStatusData(guma ? { ...guma, _scanned: code } : { _notFound: true, _scanned: code });
        setGumaStatusLoading(false);
      })
      .catch((err) => {
        err?.json?.().then((e) =>
          toast.current.show({ severity: 'error', summary: 'Greška', detail: e.message, life: 3000 })
        );
        setGumaStatusVisible(false);
        setGumaStatusLoading(false);
      });
  };

  const handleVratiNaZalihu = () => {
    setVratiSubmitted(true);
    if (!vratiMagacin || vratiKilometraza == null) return;
    const g = gumaStatusData;
    setVratiLoading(true);
    fetch(process.env.REACT_APP_BACKEND_URL + '/warehouse/SaveGumaPrijemnica', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
      body: JSON.stringify({
        serBroj: g.SerBroj,
        id_artikal: g.id_artikal,
        dot: g.DOT || null,
        id_magacin: vratiMagacin,
        kilometraza: vratiKilometraza ?? 0,
      }),
    })
      .then(handleErrors)
      .then(() => {
        toast.current.show({ severity: 'success', summary: 'Guma je uspešno vraćena na zalihu!', life: 3000 });
        setVratiVisible(false);
        fetchData();
      })
      .catch((err) =>
        err?.json?.().then((e) =>
          toast.current.show({ severity: 'error', summary: 'Greška', detail: e.message, life: 3000 })
        )
      )
      .finally(() => setVratiLoading(false));
  };

  const header = (
    <div className="export-buttons fakture-stavke-header">
      <div className="fakture-stavke-radnje">
        <Button type="button" icon="pi pi-file-excel" severity="success" rounded onClick={exportExcel} data-pr-tooltip="XLS" />
        <Button type="button" icon="pi pi-qrcode" severity="secondary" rounded onClick={() => { setScannerKey(k => k + 1); setScannerVisible(true); }} data-pr-tooltip="Provjeri status gume" />
        {selectedRowId && (
          <Button type="button" icon="pi pi-trash" severity="danger" rounded onClick={handleDelete} data-pr-tooltip="Pasiviziraj" />
        )}
        {allColumns.length > 0 && (
        <MultiSelect
        value={selectedColumns}
        options={allColumns}
        onChange={(e) => {
        const sorted = originalColumnOrder.filter((k) => e.value.includes(k));
        setSelectedColumns(sorted);
        }}
        optionLabel="label"
        display="chip"
        placeholder={t('nova.odaberiteKolone')}
        style={{ width: 'fit-content', maxWidth: '100%' }}
        showSelectAll
        filter
        filterPlaceholder="Pretraži kolone"
        maxSelectedLabels={4}
        selectedItemsLabel="{0} kolona odabrano"
        />
        )}
      </div>
      <input
        type="search"
        onInput={(e) => setGlobalFilter(e.target.value.replace(/s+/g, ' ').trimStart())}
        placeholder={t('nova.phPretraga')}
        className="p-inputtext p-component"
      />
    </div>
  );

  return (
    <div className="zaposleni_table_container">
      <Toast ref={toast} />
      <ConfirmDialog />
      {load && <Loading />}


      <Tooltip target=".export-buttons>button" position="bottom" />

      <DataTable className="stavke-tabela"
        value={tableData}
        paginator
        rows={redovaPoStrani}
        rowsPerPageOptions={[25, 50, 100]}
        first={first}
        globalFilter={globalFilter}
        onPage={(e) => { setFirst(e.first); setRedovaPoStrani(e.rows); }}
        scrollable
        header={header}
        /* Telo ide po visini ekrana, ne fiksnih 600px: tako paginator ostaje
           vidljiv i skroluje se PO TABELI, a ne cela stranica. */
        scrollHeight="clamp(220px, 58vh, 760px)"
        emptyMessage={t('common.noDataTable')}
        loading={load}
        resizableColumns
        columnResizeMode="expand"
        rowClassName={rowClassName}
        style={{ cursor: 'default' }}
      >
        {selectedColumns.map((colKey) => {
          const colMeta = allColumns.find((c) => c.value === colKey);
          return (
            <Column
              key={colKey}
              field={colKey}
              header={colMeta?.label || colKey}
              sortable
              body={(rowData) => isButtonColumn(colKey) ? previewBodyTemplate(rowData) : (rowData[colKey] ?? '-')}
              style={{ minWidth: '130px' }}
            />
          );
        })}
      </DataTable>

      {/* ── Vrati na zalihu dialog ── */}
      <Dialog
        visible={vratiVisible}
        onHide={() => setVratiVisible(false)}
        header={t('magacin.vratiGumu')}
        style={{ width: '95vw', maxWidth: '480px' }}
        contentStyle={{ padding: '1rem' }}
        modal
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
            <Button label={t('nova.odustani')} icon="pi pi-times" onClick={() => setVratiVisible(false)} className="p-button-text" style={{ minHeight: 44 }} />
            <Button label={t('magacin.potvrdi')} icon="pi pi-check" loading={vratiLoading} onClick={handleVratiNaZalihu} style={{ minHeight: 44 }} />
          </div>
        }
      >
        {gumaStatusData && !gumaStatusData._notFound && (
          <>
            <div style={{ marginBottom: '1rem', padding: '0.6rem 0.8rem', background: '#f0f4ff', borderRadius: 6, borderLeft: '4px solid #3b82f6' }}>
              <span style={{ fontSize: '0.75rem', color: '#666', display: 'block', marginBottom: 2 }}>{t('magacin.serijskiBroj')}</span>
              <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '1.1rem', letterSpacing: 1, color: '#1e3a8a' }}>{gumaStatusData.SerBroj}</span>
            </div>

            <div style={{ background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: 6, padding: '0.7rem 1rem', marginBottom: '1rem', fontSize: '0.88rem', lineHeight: 2 }}>
              <div><strong>Artikal:</strong> {gumaStatusData.NazivArtikla}{gumaStatusData.DOT ? ` — DOT ${gumaStatusData.DOT}` : ''}</div>
              {gumaStatusData.VoziloRegistracija && <div><strong>Vozilo:</strong> {gumaStatusData.VoziloRegistracija}</div>}
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>
                Magacin <span style={{ color: 'red' }}>*</span>
              </label>
              <CustomDropdown
                id="vrati-magacin"
                name="id_magacin"
                optionLabel="label"
                value={vratiMagacin}
                options={magacinOptions}
                onChange={(e) => setVratiMagacin(e.target.value)}
                placeholder={t('nova.phMagacin')}
                style={{ width: '100%' }}
                invalid={vratiSubmitted && !vratiMagacin}
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 120px' }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>{t('nova.kolicina')}</label>
                <InputNumber value={1} disabled style={{ width: '100%' }} inputStyle={{ height: '44px' }} />
              </div>
              <div style={{ flex: '1 1 120px' }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>
                  Kilometraža <span style={{ color: 'red' }}>*</span>
                </label>
                <InputNumber
                  value={vratiKilometraza}
                  onValueChange={(e) => setVratiKilometraza(e.value ?? 0)}
                  useGrouping={false}
                  min={0}
                  style={{ width: '100%' }}
                  inputStyle={{ height: '44px' }}
                  invalid={vratiSubmitted && vratiKilometraza == null}
                />
              </div>
            </div>
          </>
        )}
      </Dialog>

      {/* ── Barcode scanner ── */}
      <BarcodeScanner
        key={scannerKey}
        visible={scannerVisible}
        onHide={() => setScannerVisible(false)}
        onScan={handleScan}
      />

      {/* ── Guma status dialog ── */}
      <Dialog
        visible={gumaStatusVisible}
        onHide={() => { setGumaStatusVisible(false); setGumaStatusData(null); }}
        header={t('magacin.statusGume')}
        style={{ width: '95vw', maxWidth: '480px' }}
        contentStyle={{ padding: '1rem' }}
        modal
      >
        {gumaStatusLoading && (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
          </div>
        )}

        {!gumaStatusLoading && gumaStatusData?._notFound && (
          <>
            <div style={{ marginBottom: '1rem', padding: '0.6rem 0.8rem', background: '#f0f4ff', borderRadius: 6, borderLeft: '4px solid #3b82f6' }}>
              <span style={{ fontSize: '0.75rem', color: '#666', display: 'block', marginBottom: 2 }}>{t('magacin.serijskiBroj')}</span>
              <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '1.1rem', letterSpacing: 1, color: '#1e3a8a' }}>{gumaStatusData._scanned}</span>
            </div>
            <div style={{ padding: '1rem', background: '#fff3e0', borderRadius: 6, border: '1px solid #ffcc80', color: '#e65100', fontWeight: 500 }}>
              <i className="pi pi-exclamation-triangle" style={{ marginRight: 8 }} />
              Guma nije pronađena u bazi.
            </div>
          </>
        )}

        {!gumaStatusLoading && gumaStatusData && !gumaStatusData._notFound && (() => {
          const g = gumaStatusData;
          const lokacijaBoja = g.id_vozilo ? '#e3f2fd' : g.idKlijent ? '#fce4ec' : '#e8f5e9';
          const lokacijaBorder = g.id_vozilo ? '#1565c0' : g.idKlijent ? '#c62828' : '#2e7d32';
          const lokacijaIcon = g.id_vozilo ? 'pi-truck' : g.idKlijent ? 'pi-user' : 'pi-home';
          return (
            <>
              <div style={{ marginBottom: '1rem', padding: '0.6rem 0.8rem', background: '#f0f4ff', borderRadius: 6, borderLeft: '4px solid #3b82f6' }}>
                <span style={{ fontSize: '0.75rem', color: '#666', display: 'block', marginBottom: 2 }}>{t('magacin.serijskiBroj')}</span>
                <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '1.1rem', letterSpacing: 1, color: '#1e3a8a' }}>{g.SerBroj}</span>
              </div>

              <div style={{ padding: '0.8rem 1rem', background: lokacijaBoja, borderRadius: 6, border: `1px solid ${lokacijaBorder}`, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <i className={`pi ${lokacijaIcon}`} style={{ fontSize: '1.4rem', color: lokacijaBorder }} />
                <span style={{ fontWeight: 700, fontSize: '1rem', color: lokacijaBorder }}>{g.Lokacija}</span>
              </div>

              <div style={{ background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: 6, padding: '0.7rem 1rem', fontSize: '0.9rem', lineHeight: 2.2 }}>
                <div><strong>Artikal:</strong> {g.NazivArtikla}{g.DOT ? ` — DOT ${g.DOT}` : ''}</div>
                {g.VoziloRegistracija && <div><strong>Vozilo:</strong> {g.VoziloRegistracija}</div>}
                {g.KlijentNaziv && <div><strong>Klijent:</strong> {g.KlijentNaziv}</div>}
                {g.KlijentRegistracija && <div><strong>Reg. klijenta:</strong> {g.KlijentRegistracija}</div>}
                <div><strong>Kilometraža:</strong> {g.Kilometraza ?? 0} km</div>
                <div><strong>Ažurirano:</strong> {g.Azurirano}</div>
              </div>

              {g.id_vozilo && (
                <div style={{ marginTop: '1rem' }}>
                  <Button
                    label={t('magacin.vratiNaZalihu')}
                    icon="pi pi-reply"
                    className="p-button-warning"
                    style={{ width: '100%' }}
                    onClick={() => {
                      setVratiMagacin(null);
                      setVratiKilometraza(g.Kilometraza ?? 0);
                      setVratiSubmitted(false);
                      setGumaStatusVisible(false);
                      setVratiVisible(true);
                    }}
                  />
                </div>
              )}
            </>
          );
        })()}
      </Dialog>
    </div>
  );
};

export default PregledGuma;
